'use client'

import { useCallback, useMemo, useSyncExternalStore } from 'react'

import { paintColorMode, pickSharedColorMode, readSharedColorMode, writeSharedColorMode } from './shared-color-mode'

const DEFAULT_STORAGE_KEY = 'theme'

export interface UseDarkModeOptions {
  /** localStorage key to write 'light' / 'dark' under. Defaults to 'theme'. */
  storageKey?: string
}

export interface UseDarkModeResult {
  /** True when `<html>` currently has the `dark` class. */
  isDark: boolean
  /** Persist the choice + flip the `<html>` class. The MutationObserver
   *  in the subscription notifies all consumers, so no manual dispatch. */
  setDark: (dark: boolean) => void
}

function getIsDarkSnapshot(): boolean {
  if (typeof document === 'undefined') {
    return false
  }
  return document.documentElement.classList.contains('dark')
}

function getServerSnapshot(): boolean {
  return false
}

function applyClass(dark: boolean): void {
  if (typeof document === 'undefined') {
    return
  }
  const root = document.documentElement
  root.classList.remove('light', 'dark')
  root.classList.add(dark ? 'dark' : 'light')
}

// ------------------------------------------------------------------
// Three-way color mode (PROD-986)
// ------------------------------------------------------------------

/** Mirrors `ColorModePreference` in `@ordo/auth`, and the `allowed_values`
 *  of multipass's registered `color_mode` ConfigKey. `@ordo/ui` must not
 *  depend on `@ordo/auth`, so the union is restated rather than imported. */
export type ColorMode = 'light' | 'dark' | 'system'

/** The app-side proxy that `createUserConfigRoutes` from `@ordo/auth/express`
 *  mounts under each app's `/api/auth`. Same path in every app by
 *  convention, so the hook needs no per-app wiring. */
const DEFAULT_PERSIST_URL = '/api/auth/me/config/color_mode'

export function isColorMode(value: unknown): value is ColorMode {
  return value === 'light' || value === 'dark' || value === 'system'
}

function prefersDark(): boolean {
  if (typeof window === 'undefined') {
    return false
  }
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false
}

/** The concrete theme a preference renders as right now. */
export function resolveColorMode(preference: ColorMode): 'light' | 'dark' {
  if (preference === 'system') {
    return prefersDark() ? 'dark' : 'light'
  }
  return preference
}

/** Exported for tests: the exact precedence the control's highlighted
 *  segment follows — this device's stored value, else the account
 *  preference, else 'system'. */
export function readPreference(storageKey: string, fallback: ColorMode = 'system'): ColorMode {
  try {
    const stored = window.localStorage?.getItem(storageKey)
    if (isColorMode(stored)) {
      return stored
    }
  } catch {
    // private mode / blocked storage — fall through to the default.
  }
  return fallback
}

/**
 * Re-apply the `<html>` class when the OS flips, but only while the
 * user is actually following the system. Registered once per storage
 * key at module scope rather than per component: it is global browser
 * state, and N mounted consumers must not mean N listeners.
 */
const systemFollowers = new Set<string>()

function followSystem(storageKey: string, read: () => ColorMode = () => readPreference(storageKey)): void {
  if (systemFollowers.has(storageKey) || typeof window === 'undefined') {
    return
  }
  const mql = window.matchMedia?.('(prefers-color-scheme: dark)')
  if (!mql?.addEventListener) {
    return
  }
  systemFollowers.add(storageKey)
  mql.addEventListener('change', () => {
    if (read() === 'system') {
      applyClass(mql.matches)
    }
  })
}

function subscribe(callback: () => void): () => void {
  if (typeof window === 'undefined') {
    return () => {}
  }
  const cleanups: Array<() => void> = []

  const onStorage = () => callback()
  window.addEventListener('storage', onStorage)
  cleanups.push(() => window.removeEventListener('storage', onStorage))

  const mql = window.matchMedia?.('(prefers-color-scheme: dark)')
  if (mql) {
    const onMq = () => callback()
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onMq)
      cleanups.push(() => mql.removeEventListener('change', onMq))
    } else if (typeof (mql as unknown as { addListener?: (cb: () => void) => void }).addListener === 'function') {
      const legacy = mql as unknown as {
        addListener: (cb: () => void) => void
        removeListener: (cb: () => void) => void
      }
      legacy.addListener(onMq)
      cleanups.push(() => legacy.removeListener(onMq))
    }
  }

  if (typeof MutationObserver !== 'undefined' && document.documentElement) {
    const mo = new MutationObserver(callback)
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
    cleanups.push(() => mo.disconnect())
  }

  return () => {
    for (const fn of cleanups) {
      fn()
    }
  }
}

/**
 * Reads the current rendered theme from `<html class>` and stays in
 * sync across:
 *   - cross-tab `storage` events
 *   - OS `prefers-color-scheme` changes
 *   - any code path that flips the `<html>` class (app bootstrap
 *     scripts, server-default hydration, browser extensions)
 *
 * The switch in `AppSidebarUserMenu` should mirror what the user is
 * seeing, not what's stored — those can diverge during system-following
 * mode or before async preference hydration completes.
 */
export function useDarkMode(opts: UseDarkModeOptions = {}): UseDarkModeResult {
  const storageKey = opts.storageKey ?? DEFAULT_STORAGE_KEY
  const isDark = useSyncExternalStore(subscribe, getIsDarkSnapshot, getServerSnapshot)

  const setDark = useCallback(
    (dark: boolean) => {
      try {
        window.localStorage?.setItem(storageKey, dark ? 'dark' : 'light')
      } catch {
        // private mode / quota — ignore; class change still applies for the session.
      }
      applyClass(dark)
    },
    [storageKey],
  )

  return { isDark, setDark }
}

export interface UseColorModeOptions {
  /** localStorage key holding the raw preference. Defaults to 'theme'. */
  storageKey?: string
  /**
   * App-side endpoint the preference is PUT to as `{ value }`. Defaults
   * to the shared `/api/auth/me/config/color_mode` proxy. Pass `null` to
   * keep the choice device-local (unauthenticated surfaces).
   */
  persistUrl?: string | null
  /**
   * What the preference is when this device has no local value — pass
   * `getColorMode(user)` from `@ordo/auth`. Without it the control would
   * read 'system' while `applyDefaultThemePreference` had already painted
   * the user's stored preference, so the highlighted segment and the
   * actual page would disagree.
   *
   * Deliberately does NOT write localStorage: leaving this device's slot
   * empty is what keeps the app tracking the account preference on every
   * load instead of freezing the first value it ever saw.
   */
  fallback?: ColorMode | null
  /**
   * Internal apps pass `true`: the choice is shared with every other internal
   * app through the `.ordo.com` cookie (`./shared-color-mode`), wins over
   * this app's own stored value, and is re-read when the tab regains focus.
   * Off by default so the customer-facing Ordo app keeps its own theme.
   */
  shared?: boolean
}

export interface UseColorModeResult {
  /** What the user chose — 'system' stays 'system'. Drives the control. */
  preference: ColorMode
  /** What is actually rendered right now. Read off `<html>`, so it stays
   *  truthful even if a bootstrap script or extension set the class. */
  resolved: 'light' | 'dark'
  /** Persist locally + to multipass, and repaint. */
  setPreference: (mode: ColorMode) => void
}

function subscribePreference(storageKey: string, read?: () => ColorMode, shared = false) {
  return (callback: () => void) => {
    followSystem(storageKey, read)
    const unsubscribe = subscribe(callback)
    if (!shared || typeof document === 'undefined') {
      return unsubscribe
    }
    // Another app may have changed the shared choice while this tab sat in
    // the background. Repaint to it when the tab comes back.
    const onVisible = () => {
      if (document.visibilityState !== 'visible') {
        return
      }
      const mode = readSharedColorMode()
      if (mode) {
        paintColorMode(mode)
        callback()
      }
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      document.removeEventListener('visibilitychange', onVisible)
      unsubscribe()
    }
  }
}

/**
 * Three-way Light / Dark / System color mode, shared by every internal
 * app (PROD-986).
 *
 * The preference is the user's, not the device's: `setPreference` writes
 * it back to multipass (`config.color_mode`) as well as to localStorage,
 * so choosing Dark in one app is the default in the others. localStorage
 * remains the pre-paint cache that the FOUC script reads, and — per the
 * shipped `applyDefaultThemePreference` contract — a value already stored
 * locally still wins on load; the server value seeds apps that have none.
 *
 * The multipass write is fire-and-forget: a failed PUT must never block
 * or revert the repaint the user just asked for. Worst case the choice
 * stays device-local, which is exactly the pre-PROD-986 behavior.
 */
export function useColorMode(opts: UseColorModeOptions = {}): UseColorModeResult {
  const storageKey = opts.storageKey ?? DEFAULT_STORAGE_KEY
  const persistUrl = opts.persistUrl === undefined ? DEFAULT_PERSIST_URL : opts.persistUrl

  const fallback = opts.fallback ?? 'system'
  const shared = opts.shared === true
  const account = opts.fallback ?? null

  const getPreference = useCallback((): ColorMode => {
    if (!shared) {
      return readPreference(storageKey, fallback)
    }
    let local: string | null = null
    try {
      local = window.localStorage?.getItem(storageKey) ?? null
    } catch {
      // private mode / blocked storage
    }
    return pickSharedColorMode({ shared: readSharedColorMode(), account, local })
  }, [storageKey, fallback, shared, account])
  const subscribeFn = useMemo(
    () => subscribePreference(storageKey, getPreference, shared),
    [storageKey, getPreference, shared],
  )
  const preference = useSyncExternalStore(subscribeFn, getPreference, () => fallback)
  const isDark = useSyncExternalStore(subscribeFn, getIsDarkSnapshot, getServerSnapshot)

  const setPreference = useCallback(
    (mode: ColorMode) => {
      try {
        window.localStorage?.setItem(storageKey, mode)
      } catch {
        // private mode / quota — the class change below still applies.
      }
      if (shared) {
        writeSharedColorMode(mode)
      }
      applyClass(resolveColorMode(mode) === 'dark')
      if (!persistUrl) {
        return
      }
      void fetch(persistUrl, {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ value: mode }),
        credentials: 'same-origin',
      }).catch(() => {
        // Offline, signed out, or multipass down. The local choice stands.
      })
    },
    [storageKey, persistUrl, shared],
  )

  return { preference, resolved: isDark ? 'dark' : 'light', setPreference }
}
